<template>
  <div>
    <template v-if="!isMobile">
      <!-- Desktop version -->
      <v-card class="mx-auto" :flat="flat">
        <v-window v-model="secondWindow">
          <!-- First step in the top level window -->
          <v-window-item :value="bookingScreenEnum.START">
            <v-row class="mx-0" style="overflow: hidden">
              <v-col cols="6" :style="`background-color: ${bgColor} `">
                <div class="d-flex fill-height justify-center align-center">
                  <booking-start
                    :bookingIframe="bookingIframe"
                    v-if="step == bookingStepEnum.START && bookingIframe"
                    :dark="dark"
                    :bgColor="bgColor"
                  ></booking-start>
                  <booking-calendar
                    v-if="step == bookingStepEnum.CALENDER"
                    v-on:date-change="onDateChange"
                    :dark="dark"
                  ></booking-calendar>
                </div>
              </v-col>

              <!-- If not in iframe, show booking services on full width (12 cols) -->
              <v-col :cols="step == bookingStepEnum.START && !bookingIframe ? 12 : 6">
                <v-window v-model="step">
                  <!-- First step in the second level window -->
                  <v-window-item :value="bookingStepEnum.START">
                    <booking-services :bookingIframe="bookingIframe"></booking-services>
                  </v-window-item>
                  <!-- Second step in the second level window -->
                  <v-window-item :value="bookingStepEnum.CALENDER">
                    <booking-slots></booking-slots>
                  </v-window-item>
                  <v-window-item :value="bookingStepEnum.FORM"></v-window-item>
                </v-window>
              </v-col>
            </v-row>
          </v-window-item>
          <!-- Third Step in the top level window -->
          <v-window-item :value="bookingScreenEnum.FORM">
            <booking-form @submit="onBookSlot"></booking-form>
          </v-window-item>
          <v-window-item :value="bookingScreenEnum.SUCCESS">
            <booking-success></booking-success>
          </v-window-item>
        </v-window>
      </v-card>
      <v-row class="mx-4 mb-4 hidden-sm-and-down" v-if="showDesktopBackButton">
        <v-col cols="6">
          <v-btn outlined :color="color" @click="goBackToStart">{{ $t("bookingForm.common.prev") }}</v-btn>
        </v-col>
      </v-row>
    </template>

    <!-- Mobile version -->
    <template v-else>
      <v-card :flat="flat">
        <v-window v-model="step">
          <v-window-item :value="bookingStepEnum.START" :style="`background-color: ${bgColor} `">
            <booking-start :isMobile="true" :dark="dark" :bgColor="bgColor"></booking-start>
          </v-window-item>
          <v-window-item :value="bookingStepEnum.SERVICES">
            <booking-services :isMobile="true"></booking-services>
          </v-window-item>
          <v-window-item :value="bookingStepEnum.CALENDER">
            <booking-calendar :isMobile="true" v-on:date-change="onDateChange"></booking-calendar>
          </v-window-item>
          <v-window-item :value="bookingStepEnum.SLOTS">
            <booking-slots :isMobile="true" class="pb-16"></booking-slots>
          </v-window-item>
          <v-window-item :value="bookingStepEnum.FORM">
            <booking-form :isMobile="true" @submit="onBookSlot" class="pb-16"></booking-form>
          </v-window-item>
          <v-window-item :value="bookingStepEnum.SUCCESS">
            <booking-success></booking-success>
          </v-window-item>
        </v-window>
      </v-card>
    </template>
  </div>
</template>

<script lang="ts">
import BookingCalendar from "@/components/booking/customer/BookingCalendar.vue";
import BookingForm from "@/components/booking/customer/BookingForm.vue";
import BookingServices from "@/components/booking/customer/BookingServices.vue";
import BookingSlots from "@/components/booking/customer/BookingSlots.vue";
import BookingStart from "@/components/booking/customer/BookingStart.vue";
import LayoutSimple from "@/layouts/LayoutSimple.vue";
import {
  BookingServiceBookingViewModelGen,
  BookingCreateBookingWithoutResourceDtoGen
} from "@/services/booking/v1/data-contracts";
import { BookingScreenEnum, BookingStepEnum } from "@/store/enum/bookingScreenEnum";
import { AvailabilityModule } from "@/store/modules/availability.store";
import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import BookingSuccess from "./BookingSuccess.vue";
import { handleError } from "@/lib/utility/handleError";
import { ReportModule } from "@/store/modules/report.store";
import { PartnerModule } from "@/store/modules/partner";
import { NotFoundException } from "@/lib/exceptions/http";
import { BookingCreateModule } from "@/store/modules/booking-create.store";
import { UTMModule } from "@/store/modules/utm.store";
import { getDefaultPartnerColor } from "@/lib/getDefaultPartnerColor";
import { ConfigModule } from "@/store/modules/config";

@Component({
  components: {
    LayoutSimple,
    BookingStart,
    BookingCalendar,
    BookingServices,
    BookingSlots,
    BookingForm,
    BookingSuccess
  }
})
export default class BookingProcess extends Vue {
  /**
   * Navigation on Desktop:
   * Start (and Services) -> Calender (and Slots) -> Form
   *
   * Navigation on Mobile:
   * Start -> Services -> Calender -> Slots -> Form
   */

  bookingScreenEnum = BookingScreenEnum;
  bookingStepEnum = BookingStepEnum;

  @Prop({ default: false })
  flat!: boolean;

  @Prop({ default: "" })
  bgColor!: string;

  @Prop({ default: false })
  dark!: boolean;

  /**
   * Flag if booking is used in an iframe(design should not change)
   */
  @Prop({ default: false })
  bookingIframe!: boolean;

  availabilityOptions: { date?: Date; isSetDate?: boolean } = { date: undefined, isSetDate: undefined };

  get partner() {
    return ReportModule.partner || PartnerModule.partner;
  }

  get partnerId() {
    return this.partner._id;
  }

  get step() {
    return AvailabilityModule.currentStep;
  }

  set step(slot: BookingStepEnum) {
    AvailabilityModule.setCurrentStep(slot);
  }

  get secondWindow() {
    return AvailabilityModule.secondWindow;
  }

  set secondWindow(value: BookingScreenEnum) {
    AvailabilityModule.setSecondWindow(value);
  }

  get bookableServices(): BookingServiceBookingViewModelGen[] {
    return AvailabilityModule.paginationList;
  }

  get isMobile() {
    switch (this.$vuetify.breakpoint.name) {
      case "xs":
        return true;
      case "sm":
        return true;
      case "md":
        return false;
      case "lg":
        return false;
      case "xl":
        return false;
      default:
        return false;
    }
  }

  get color() {
    return getDefaultPartnerColor();
  }

  get isHeadless() {
    return ConfigModule.headless;
  }

  get showDesktopBackButton() {
    return this.step === BookingStepEnum.CALENDER && !this.isHeadless;
  }

  goBackToStart() {
    AvailabilityModule.setStart();
  }

  @Watch("step")
  async fetchAvailableSlotsWhenNeeded(step: BookingStepEnum, prevStep: BookingStepEnum) {
    /**
     * fetch slots if we are landing on the calendar view or on the slots view(mobile) and we're
     * navigating back from the booking form
     */
    if (step === BookingStepEnum.CALENDER || (step === BookingStepEnum.SLOTS && prevStep === BookingStepEnum.FORM)) {
      await this.checkAvailability(this.availabilityOptions);
    }
  }

  async mounted() {
    this.availabilityOptions.isSetDate = !this.isMobile;
    await AvailabilityModule.refresh();
  }

  async checkAvailability(options: { date?: Date; isSetDate?: boolean }) {
    AvailabilityModule.setIsLoadingSlots(true);

    try {
      await AvailabilityModule.findOneByPartner({
        partnerId: this.partnerId,
        id: AvailabilityModule.serviceId,
        date: options?.date?.toISOString() || new Date().toISOString()
      });
      /**
       * Only set first date initial on desktop
       */
      if (AvailabilityModule.slots.length && options.isSetDate) {
        AvailabilityModule.setFirstAvailableDay();
      }

      if (!options.isSetDate && this.step !== BookingStepEnum.SLOTS) {
        AvailabilityModule.setDate(null);
      }
    } catch (error) {
      if (error instanceof NotFoundException) {
        Vue.$toast.error(String(this.$t("bookingForm.BookingProcess.serviceNotFound")));
        AvailabilityModule.setServices();
      } else {
        handleError(error);
      }
    } finally {
      AvailabilityModule.setIsLoadingSlots(false);
    }
  }

  async onDateChange(date: Date) {
    this.availabilityOptions = {
      ...this.availabilityOptions,
      date
    };

    /** Fetch availability when changing dates in the calendar */
    await this.checkAvailability(this.availabilityOptions);
  }

  async onBookSlot(booking: BookingCreateBookingWithoutResourceDtoGen) {
    AvailabilityModule.setIsloadingServices(true);
    AvailabilityModule.setBookingFormDisabled(true);

    if (
      BookingCreateModule.rwgToken &&
      BookingCreateModule.rwgTime &&
      BookingCreateModule.rwgTime > new Date().getTime() - 30 * 24 * 60 * 60 * 1000
    ) {
      booking.rwgToken = BookingCreateModule.rwgToken;
    }

    booking.utm = UTMModule.utm;

    try {
      await AvailabilityModule.create({ partnerId: this.partnerId, dto: booking });

      // redirect to booking success view
      AvailabilityModule.setSuccess();
    } catch (error) {
      if (error instanceof NotFoundException) {
        Vue.$toast.error(String(this.$t("bookingForm.BookingProcess.slotMissingErrorMessage")));
      } else {
        handleError(error);
      }
    } finally {
      AvailabilityModule.setIsloadingServices(false);
      AvailabilityModule.setBookingFormDisabled(false);
    }
  }
}
</script>
